import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Shield, Clock, ArrowRight, Wrench, Star, MapPin, Phone, Mail } from 'lucide-react';
import './AboutPage.css';  // if file name is Aboutpage.css

const TEAM = [
  { name: 'Gurkirat Singh',    role: 'Founder & CEO',        initials: 'VA', exp: '10 yrs exp' },
  { name: 'Ishpreet Singh',    role: 'Founder & CEO',        initials: 'VA', exp: '10 yrs exp' },
  { name: 'Rajesh Kumar',    role: 'Lead Technician',       initials: 'RK', exp: '12 yrs exp' },
  { name: 'Amandeep Singh',  role: 'Senior Mechanic',       initials: 'AS', exp: '9 yrs exp'  },
  { name: 'Sunil Verma',     role: 'Detailing Specialist',  initials: 'SV', exp: '7 yrs exp'  },
  { name: 'Priya Nair',      role: 'Customer Relations',    initials: 'PN', exp: '5 yrs exp'  },
  { name: 'Deepak Sharma',   role: 'Electricals Expert',    initials: 'DS', exp: '11 yrs exp' },
];

const VALUES = [
  { icon: <Shield size={24} />, title: 'Transparency',   desc: 'No hidden charges. Every part, every labour cost itemised clearly before work begins.' },
  { icon: <Award size={24} />,  title: 'Excellence',     desc: 'We do not cut corners. OEM parts, certified techniques, and a 30-day guarantee on all work.' },
  { icon: <Users size={24} />,  title: 'People First',   desc: 'From doorstep pickup to real-time updates, every touchpoint is designed around your convenience.' },
  { icon: <Clock size={24} />,  title: 'Punctuality',    desc: 'We respect your time. If we miss a committed deadline, we compensate — no questions asked.' },
];

export default function AboutPage() {
  return (
    <div className="about-page" style={{ paddingTop: 72 }}>

      {/* Hero */}
      <section className="about-hero">
        <div className="about-hero__bg" aria-hidden />
        <div className="container about-hero__inner">
          <div className="about-hero__content animate-fade-up">
            <div className="accent-line" style={{ display: 'block', marginBottom: 16 }} />
            <h1 className="about-hero__title">
              Built on Passion.<br />
              <em>Driven by Precision.</em>
            </h1>
            <p className="about-hero__text">
              AutoElite is a Proud branch of Balwinder Motors which was founded in 2022 with a single belief: 
              that car servicing can be a relationship, not a transaction. We blend old-school craftsmanship 
              with modern technology to deliver a service experience that’s as seamless as it is meticulous.
              We are not just mechanics; we are caretakers of your vehicle, committed to keeping it in peak
              condition while earning your trust mile after mile.
            </p>
            <p className="about-hero__text">
              What started as a four-bay workshop in Patiala has grown into a 5,000 sq.ft state-of-the-art
              facility serving hundreds of vehicles every year — while keeping the same personal, meticulous
              attention we started with.
            </p>
            <Link to="/booking" className="btn btn-primary" style={{ marginTop: 24 }}>
              Book a Service <ArrowRight size={16} />
            </Link>
          </div>
          <div className="about-hero__stats animate-fade-up" style={{ animationDelay: '0.2s' }}>
            {[
              { value: '2016', label: 'Founded' },
              { value: '12K+', label: 'Vehicles Served' },
              { value: '15',   label: 'Expert Team' },
              { value: '98%',  label: 'Client Retention' },
            ].map(({ value, label }) => (
              <div key={label} className="about-stat">
                <span className="about-stat__value">{value}</span>
                <span className="about-stat__label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section">
        <div className="container">
          <div className="section-header" style={{ textAlign: 'center', marginBottom: 56 }}>
            <div className="accent-line" style={{ display: 'block', margin: '0 auto 16px' }} />
            <h2 className="section-title" style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.8rem, 3vw, 2.2rem)', fontWeight: 500, marginBottom: 12 }}>What We Stand For</h2>
          </div>
          <div className="values-grid stagger">
            {VALUES.map(({ icon, title, desc }) => (
              <div key={title} className="value-card card animate-fade-up">
                <div className="value-card__icon">{icon}</div>
                <h3 className="value-card__title">{title}</h3>
                <p className="value-card__desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Timeline */}
      <section className="about-timeline section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div className="accent-line" style={{ display: 'block', margin: '0 auto 16px' }} />
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.8rem, 3vw, 2.2rem)', fontWeight: 500 }}>Our Journey</h2>
          </div>
          <div className="timeline">
            {[
              { year: '2016', event: 'Balwinder Motors founded in Patiala with 4 bays and a 5-person team.' },
              { year: '2018', event: 'Expanded to 8 bays. Introduced doorstep pickup and drop services.' },
              { year: '2020', event: 'Launched digital service tracking. Weathered COVID with zero layoffs.' },
              { year: '2022', event: 'Launched AutoElite with our new 12,000 sq.ft facility. Introduced ceramic coating & detailing bay.' },
              { year: '2024', event: 'AutoElite app launched. Surpassed 1,000 vehicles serviced milestone.' },
              { year: '2025', event: 'Expanding to two new locations across Patiala, Punjab. Premium fleet contracts.' },
            ].map(({ year, event }, i) => (
              <div key={year} className={`timeline-item ${i % 2 === 0 ? 'left' : 'right'}`}>
                <div className="timeline-dot" />
                <div className="timeline-content card">
                  <span className="timeline-year">{year}</span>
                  <p className="timeline-event">{event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div className="accent-line" style={{ display: 'block', margin: '0 auto 16px' }} />
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.8rem, 3vw, 2.2rem)', fontWeight: 500 }}>Meet the Team</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 15, marginTop: 10 }}>The hands behind every perfect service.</p>
          </div>
          <div className="team-grid stagger">
            {TEAM.map(({ name, role, initials, exp }) => (
              <div key={name} className="team-card card animate-fade-up">
                <div className="team-card__avatar">{initials}</div>
                <h3 className="team-card__name">{name}</h3>
                <p className="team-card__role">{role}</p>
                <div className="team-card__exp">
                  <Star size={11} fill="var(--accent)" color="var(--accent)" />
                  <span>{exp}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section className="about-contact section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="about-contact__grid">
            <div>
              <div className="accent-line" style={{ display: 'block', marginBottom: 16 }} />
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '2rem', fontWeight: 500, marginBottom: 24 }}>Visit Us</h2>
              <div className="contact-items">
                <div className="contact-item">
                  <MapPin size={18} />
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 14 }}>AutoElite Service Centre</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>#24, Kissan Market, Sirhind Road, Patiala, Punjab 141001</p>
                  </div>
                </div>
                <div className="contact-item">
                  <Phone size={18} />
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 14 }}>+91 77194 12031</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Mon–Sat: 8AM–8PM · Sun: 10AM–4PM</p>
                  </div>
                </div>
                <div className="contact-item">
                  <Mail size={18} />
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 14 }}>Gurkirat@autoelite.in</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>We respond within 2 hours</p>
                  </div>
                </div>
                <div className="contact-item">
                  <Wrench size={18} />
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 14 }}>Certified Workshop</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>ICAR Certified · ISO 9001:2015</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="about-map-placeholder">
              <div className="about-map-inner">
                <MapPin size={32} style={{ color: 'var(--accent)', marginBottom: 12 }} />
                <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>#24, Kissan Market</p>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 }}>Sirhind Road, Patiala, Punjab 147001</p>
                <a
                  href="https://maps.app.goo.gl/k6ccBDxLw9umXH9d7"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline"
                  style={{ fontSize: 12 }}
                >
                  Open in Maps <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}